import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEncuestasPendientes, responderEncuesta } from '../../redux/encuestasSlice';
import {
  Button,
  Modal,
  Form,
  Alert,
  Card,
  Container,
  Row,
  Col,
  Placeholder,
  Spinner,
  Stack,
  Badge
} from 'react-bootstrap';
import Swal from 'sweetalert2';
import '../../styles/RencuestaPage.css';

// --- Normalizador defensivo para alternativas ---
// Asegura que siempre tengamos un Array para poder usar .map() sin romper el render.
export const normalizeAlternativas = (value) => {
  if (value == null) return [];
  if (Array.isArray(value)) return value;

  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return [];
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object') return Object.values(parsed);
      return [s];
    } catch (e) {
      // "a,b,c" -> fallback a lista
      if (s.includes(',')) return s.split(',').map(v => v.trim()).filter(Boolean);
      return [s];
    }
  }

  if (typeof value === 'object') {
    try {
      return Array.isArray(value) ? value : Object.values(value);
    } catch (e) {
      return [];
    }
  }
  return [];
};
// --- fin normalizador ---


const RencuestaPage = () => {
  const dispatch = useDispatch();
  const { encuestasPendientes = [], loading, error } = useSelector((state) => state.encuestas);

  const [showModal, setShowModal] = useState(false);
  const [selectedEncuesta, setSelectedEncuesta] = useState(null);
  const [preguntas, setPreguntas] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [responseError, setResponseError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    dispatch(fetchEncuestasPendientes());
  }, [dispatch]);

  const handleOpenModal = (encuesta) => {
    const preguntasEncuesta = encuesta.preguntasDeLaEncuesta || encuesta.PreguntasEncuestas || [];
    
    setSelectedEncuesta(encuesta);
    setPreguntas(preguntasEncuesta);

    const initialRespuestas = {};
    preguntasEncuesta.forEach(preg => {
      initialRespuestas[preg.id] = null;
    });

    setRespuestas(initialRespuestas);
    setResponseError(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEncuesta(null);
    setPreguntas([]);
    setRespuestas({});
    setResponseError(null);
  };

  const handleRespuestaChange = (preguntaId, value) => {
    setRespuestas(prev => ({
      ...prev,
      [preguntaId]: value,
    }));
  };

  const todasRespuestasCompletas = () => {
    return preguntas.every(pregunta => {
      const respuesta = respuestas[pregunta.id];

      // Verificar que la respuesta no sea null o undefined
      if (respuesta === null || respuesta === undefined) {
        return false;
      }

      // Para preguntas de texto, verificar que no esté vacía
      if (pregunta.tipo_respuesta !== 'opcion_multiple') {
        return typeof respuesta === 'string' && respuesta.trim() !== '';
      }

      // Para opción múltiple, verificar que tenga un valor válido
      return true;
    });
  };

  const handleEnviarRespuestas = async () => {
    if (!selectedEncuesta || !todasRespuestasCompletas()) return;

    setIsSubmitting(true);
    setResponseError(null);

    try {
      const respuestasArray = Object.entries(respuestas).map(([preguntaId, respuesta]) => ({
        pregunta_id: parseInt(preguntaId, 10),
        respuesta: typeof respuesta === 'string' ? respuesta.trim() : respuesta,
      }));

      const promises = respuestasArray.map((respuesta) =>
        dispatch(
          responderEncuesta({
            encuestaId: selectedEncuesta.id,
            respuesta: { pregunta_id: respuesta.pregunta_id, respuesta: respuesta.respuesta },
          })
        ).unwrap()
      );

      await Promise.all(promises);

      Swal.fire({
        icon: 'success',
        title: '¡Respuestas enviadas!',
        text: 'Tus respuestas han sido registradas correctamente.',
        confirmButtonColor: '#3085d6',
      });

      dispatch(fetchEncuestasPendientes());
      handleCloseModal();
    } catch (err) {
      console.error('Error al enviar respuestas:', err);
      setResponseError(err.message || 'Ocurrió un error al enviar las respuestas.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderPlaceholders = () => (
    <Row>
      {[1, 2, 3].map((item) => (
        <Col md={6} lg={4} key={item} className="mb-4">
          <Card className="h-100">
            <Card.Body>
              <Placeholder as={Card.Title} animation="wave">
                <Placeholder xs={8} />
              </Placeholder>
              <Placeholder as={Card.Text} animation="wave">
                <Placeholder xs={10} /> <Placeholder xs={8} /> <Placeholder xs={6} />
              </Placeholder>
              <Placeholder.Button variant="primary" xs={6} />
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );

  const renderEncuestaCard = (encuesta) => {
    const preguntasEncuesta = encuesta.preguntasDeLaEncuesta || encuesta.PreguntasEncuestas || [];
    
    return (
      <Col md={6} lg={4} key={encuesta.id} className="mb-4">
        <Card className="h-100 shadow-sm hover-shadow">
          <Card.Body className="d-flex flex-column">
            <Stack direction="horizontal" gap={2} className="mb-2">
              <Badge bg="primary" pill>
                {preguntasEncuesta.length} preguntas
              </Badge>
              <Badge bg="secondary" pill>
                {new Date(encuesta.fecha_creacion).toLocaleDateString()}
              </Badge>
            </Stack>
            <Card.Title className="text-primary">{encuesta.titulo}</Card.Title>
            <Card.Text className="text-muted flex-grow-1">
              {encuesta.descripcion || 'Sin descripción'}
            </Card.Text>
            <Button
              variant="primary"
              onClick={() => handleOpenModal(encuesta)}
              className="mt-auto align-self-start"
            >
              <i className="bi bi-pencil-square me-2"></i>
              Responder
            </Button>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  const renderPregunta = (pregunta, index) => {
    return (
      <Form.Group
        key={pregunta.id}
        className="mb-4 p-3 bg-light rounded"
        controlId={`pregunta-${pregunta.id}`}
      >
        <Form.Label className="fw-bold">
          {index + 1}. {pregunta.pregunta}
          {pregunta.tipo_respuesta !== 'opcion_multiple' && (
            <span className="text-danger"> *</span>
          )}
        </Form.Label>

        {pregunta.tipo_respuesta === 'opcion_multiple' ? (
          <div className="mt-2">
            {normalizeAlternativas(pregunta.alternativas).map((alternativa, altIndex) => (
              <div
                key={altIndex}
                className="form-check-option mb-2 p-2 rounded"
                tabIndex="0"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleRespuestaChange(pregunta.id, alternativa);
                  }
                }}
              >
                <Form.Check
                  type="radio"
                  id={`pregunta-${pregunta.id}-opcion-${altIndex}`}
                  name={`pregunta-${pregunta.id}`}
                  value={alternativa}
                  label={alternativa}
                  checked={respuestas[pregunta.id] === alternativa}
                  onChange={() => handleRespuestaChange(pregunta.id, alternativa)}
                  className="form-check-lg"
                />
              </div>
            ))}
          </div>
        ) : (
          <>
            <Form.Control
              type="text"
              placeholder="Escribe tu respuesta aquí..."
              value={respuestas[pregunta.id] || ''}
              onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
              className="mt-2"
              isInvalid={respuestas[pregunta.id] !== null && !respuestas[pregunta.id]?.trim()}
            />
            <Form.Control.Feedback type="invalid">
              Por favor ingresa una respuesta
            </Form.Control.Feedback>
          </>
        )}
      </Form.Group>
    );
  };

  return (
    <Container className="mt-4 mb-5">
      <h1 className="mb-4 text-center text-primary">Encuestas Pendientes</h1>

      {error && typeof error === 'string' && (
        <Alert variant="danger" className="text-center">
          <strong>Error:</strong> {error}
          <div className="mt-2">
            <Button variant="outline-danger" size="sm" onClick={() => dispatch(fetchEncuestasPendientes())}>
              Reintentar
            </Button>
          </div>
        </Alert>
      )}

      {!loading && encuestasPendientes.length === 0 && (
        <Alert variant="info" className="text-center">
          <i className="bi bi-check-circle-fill me-2"></i>
          No tienes encuestas pendientes en este momento.
        </Alert>
      )}

      {loading ? renderPlaceholders() : (
        <Row>
          {Array.isArray(encuestasPendientes) && encuestasPendientes.map(renderEncuestaCard)}
        </Row>
      )}

      {/* Modal de respuesta */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        backdrop="static"
        keyboard={false}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <i className="bi bi-clipboard2-pulse me-2"></i>
            {selectedEncuesta?.titulo || 'Encuesta'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {responseError && (
            <Alert variant="danger" dismissible onClose={() => setResponseError(null)}>
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {responseError}
            </Alert>
          )}

          <p className="text-muted mb-4">{selectedEncuesta?.descripcion}</p>

          {preguntas.length > 0 ? (
            <Form>
              {(Array.isArray(preguntas) ? preguntas : []).map(renderPregunta)}
            </Form>
          ) : (
            <Alert variant="info" className="text-center">
              No hay preguntas disponibles para esta encuesta.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseModal}>
            <i className="bi bi-x-circle me-2"></i>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleEnviarRespuestas}
            disabled={!todasRespuestasCompletas() || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Enviando...
              </>
            ) : (
              <>
                <i className="bi bi-send-check me-2"></i>
                Enviar Respuestas
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default RencuestaPage;